import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { VaultKeTheme } from '../utils/theme';

const AnimatedCounter: React.FC<{ end: number; suffix?: string; duration?: number }> = ({
  end,
  suffix = '',
  duration = 2000
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutProgress = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOutProgress * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return <span>{count}{suffix}</span>;
};

const About: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles: { [key: string]: React.CSSProperties } = {
    about: {
      padding: `${VaultKeTheme.spacing.xxl} ${VaultKeTheme.spacing.xl}`,
      background: `linear-gradient(135deg, ${VaultKeTheme.colors.background} 0%, ${VaultKeTheme.colors.surface} 100%)`,
      position: 'relative',
      overflow: 'hidden',
    },
    backgroundPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.03,
      backgroundImage: `
        radial-gradient(circle at 10% 90%, ${VaultKeTheme.colors.primary} 0%, transparent 50%),
        radial-gradient(circle at 90% 10%, ${VaultKeTheme.colors.primaryLight} 0%, transparent 50%)
      `,
      zIndex: 1,
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: VaultKeTheme.spacing.xxl,
      alignItems: 'center',
      position: 'relative',
      zIndex: 2,
    },
    content: {
      color: VaultKeTheme.colors.text,
    },
    title: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
      background: `linear-gradient(135deg, ${VaultKeTheme.colors.primary} 0%, ${VaultKeTheme.colors.text} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      fontWeight: '700',
    },
    description: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      marginBottom: VaultKeTheme.spacing.lg,
      lineHeight: '1.8',
      fontSize: '1.1rem',
    },
    highlight: {
      color: VaultKeTheme.colors.primary,
      fontWeight: '600',
    },
    stats: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: VaultKeTheme.spacing.lg,
      marginTop: VaultKeTheme.spacing.xl,
    },
    stat: {
      textAlign: 'center',
      padding: VaultKeTheme.spacing.lg,
      background: `linear-gradient(135deg, ${VaultKeTheme.colors.surface} 0%, ${VaultKeTheme.colors.surfaceVariant} 100%)`,
      borderRadius: VaultKeTheme.borderRadius.lg,
      border: `1px solid ${VaultKeTheme.colors.border}`,
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    },
    statNumber: {
      ...VaultKeTheme.typography.h3,
      color: VaultKeTheme.colors.primary,
      marginBottom: VaultKeTheme.spacing.sm,
      fontSize: '2rem',
      fontWeight: '700',
    },
    statLabel: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      fontSize: '0.9rem',
    },
    imageContainer: {
      position: 'relative',
      height: isMobile ? '300px' : '500px',
      borderRadius: VaultKeTheme.borderRadius.xl,
      overflow: 'hidden',
      boxShadow: `0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)`,
      background: `linear-gradient(135deg, ${VaultKeTheme.colors.surface} 0%, ${VaultKeTheme.colors.surfaceVariant} 100%)`,
    },
    imageOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(45deg, rgba(0, 212, 170, 0.15) 0%, rgba(13, 17, 23, 0.6) 100%)`,
      zIndex: 2,
    },
    imageContent: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      padding: VaultKeTheme.spacing.xl,
      background: `linear-gradient(transparent 0%, rgba(13, 17, 23, 0.9) 100%)`,
      zIndex: 3,
    },
    imageTitle: {
      ...VaultKeTheme.typography.h4,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
      fontWeight: '700',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
    },
    imageSubtitle: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      fontSize: '0.95rem',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
    },
    placeholderImage: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: isMobile ? '3rem' : '5rem',
      color: VaultKeTheme.colors.primary,
      opacity: 0.3,
      zIndex: 1,
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
      },
    },
  };

  return (
    <motion.section
      style={styles.about}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={containerVariants}
    >
      <motion.div
        style={styles.backgroundPattern}
        animate={{
          backgroundPosition: ['0% 0%', '100% 100%'],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          repeatType: 'reverse',
        }}
      ></motion.div>
      <div style={styles.container} className="about-container">
        <motion.div style={styles.content} variants={itemVariants}>
          <motion.h2 style={styles.title} variants={itemVariants}>
            Empowering Communities Through Technology
          </motion.h2>
          <motion.p style={styles.description} variants={itemVariants}>
            VaultKe was born from the vision to modernize traditional community savings groups
            (chamas) with <span style={styles.highlight}>cutting-edge technology</span>. We understand the unique challenges and
            opportunities within African communities and have built a platform that respects
            cultural traditions while embracing digital innovation.
          </motion.p>
          <motion.p style={styles.description} variants={itemVariants}>
            Our mission is to provide <span style={styles.highlight}>secure, accessible, and intelligent financial tools</span> that
            help chamas grow their collective wealth, maintain transparency, and build stronger
            communities. With VaultKe, traditional savings groups can leverage the power of
            modern finance without compromising their core values.
          </motion.p>
          <motion.div
            style={styles.stats}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.2,
                },
              },
            }}
          >
            <motion.div
              style={styles.stat}
              variants={{
                hidden: { opacity: 0, scale: 0.8, y: 20 },
                visible: { opacity: 1, scale: 1, y: 0 },
              }}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div style={styles.statNumber}>
                <AnimatedCounter end={10000} suffix="K+" />
              </div>
              <div style={styles.statLabel}>Active Chamas</div>
            </motion.div>
            <motion.div
              style={styles.stat}
              variants={{
                hidden: { opacity: 0, scale: 0.8, y: 20 },
                visible: { opacity: 1, scale: 1, y: 0 },
              }}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div style={styles.statNumber}>
                $<AnimatedCounter end={2000000} suffix="M+" />
              </div>
              <div style={styles.statLabel}>Funds Managed</div>
            </motion.div>
            <motion.div
              style={styles.stat}
              variants={{
                hidden: { opacity: 0, scale: 0.8, y: 20 },
                visible: { opacity: 1, scale: 1, y: 0 },
              }}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div style={styles.statNumber}>
                <AnimatedCounter end={50000} suffix="K+" />
              </div>
              <div style={styles.statLabel}>Community Members</div>
            </motion.div>
            <motion.div
              style={styles.stat}
              variants={{
                hidden: { opacity: 0, scale: 0.8, y: 20 },
                visible: { opacity: 1, scale: 1, y: 0 },
              }}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div style={styles.statNumber}>
                <AnimatedCounter end={99.9} suffix="%" />
              </div>
              <div style={styles.statLabel}>Uptime</div>
            </motion.div>
          </motion.div>
        </motion.div>
        <motion.div
          style={styles.imageContainer}
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div style={styles.imageOverlay}></div>
          <div style={styles.imageContent}>
            <motion.h3
              style={styles.imageTitle}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              viewport={{ once: true }}
            >
              Modern Finance, Traditional Values
            </motion.h3>
            <motion.p
              style={styles.imageSubtitle}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              viewport={{ once: true }}
            >
              Bridging the gap between heritage and innovation
            </motion.p>
          </div>
          <motion.div
            style={styles.placeholderImage}
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          >
            🏛️
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default About;